/**
 * Rocket Read's content — every question of the language's Comprehension pack, with its three
 * answers (specs/rocket-read-mode.md §6).
 *
 * Costs no new data: a `frage` word's `text` is the sentence, the second line of its `banner` is the
 * question, and the `antwort` whose `passtZu` CSV names the question's id is the right answer. The
 * other two answers are what make the mode a READING test:
 *
 * - the **rival** is the answer to the sentence's OTHER question — it is in the sentence, so only a
 *   child who took the sentence in can tell it from the right one;
 * - the **outsider** is an answer of the same kind from the same set that is NOT in the sentence, so
 *   the kind of the three (three colours, three names …) never gives the answer away.
 *
 * Phaser-free, so deal.test.ts walks every question in every shipped language.
 */
import type { Pack, PackWord } from '@/data/packs/types';
import { BUILT_IN_PACKS } from '@/data/packs';

export const COMPREHENSION_PACK_ID = 'comprehension';

export interface RocketQuestion {
  /** The `frage` id, e.g. `f1-stiel`. */
  id: string;
  /** The set it came from — its prefix is the answer kind, for analytics (`farben`, `wer`, …). */
  setId: string;
  sentence: string;
  question: string;
  right: string;
  rival: string;
  /** Candidates for the third answer: same set, neither of the two, and not in the sentence. */
  outsiders: string[];
}

export function comprehensionPack(lang: string): Pack | undefined {
  return BUILT_IN_PACKS.find((p) => p.id === COMPREHENSION_PACK_ID && p.lang === lang);
}

/**
 * Does `answer` occur in `sentence` as a word start, allowing inflection to hang off the end
 * ("grün" in "grünen")? The same match the Comprehension rival test uses
 * (src/data/packs/comprehension.test.ts, `namesAnswer`), so a sentence about *Brot* is not read as
 * naming the colour *rot*. Thai has no word starts; it matches on the plain substring.
 */
export function namesAnswer(lang: string, sentence: string, answer: string): boolean {
  if (lang === 'th') return sentence.includes(answer);
  const escaped = answer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^\\p{L}])${escaped}`, 'iu').test(sentence);
}

function fits(pack: Pack, w: PackWord): string[] {
  const raw = w[pack.forKey as string];
  return typeof raw === 'string' ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
}

/**
 * Every usable question in the pack. A question is dropped (never dealt) when it has no right
 * answer, no rival, or no outsider — deal.test.ts asserts that drops nothing in a shipped pack.
 */
export function buildQuestions(pack: Pack): RocketQuestion[] {
  const out: RocketQuestion[] = [];
  for (const set of pack.sets) {
    const frage = set.words.filter((w) => w.class === pack.bannerContextClass);
    const antwort = set.words.filter((w) => w.class === pack.candidateClass);
    const answerFor = (id: string): string | undefined =>
      antwort.find((a) => fits(pack, a).includes(id))?.text;

    for (const q of frage) {
      if (typeof q.id !== 'string' || typeof q.banner !== 'string') continue;
      const question = q.banner.split('\n')[1]?.trim();
      const right = answerFor(q.id);
      const sibling = frage.find((o) => o !== q && o.text === q.text && typeof o.id === 'string');
      const rival = sibling ? answerFor(sibling.id as string) : undefined;
      if (!question || !right || !rival || rival === right) continue;
      const outsiders = [...new Set(antwort.map((a) => a.text))].filter(
        (t) => t !== right && t !== rival && !namesAnswer(pack.lang, q.text, t),
      );
      if (outsiders.length === 0) continue;
      out.push({ id: q.id, setId: set.id, sentence: q.text, question, right, rival, outsiders });
    }
  }
  return out;
}

export interface DealtQuestion {
  q: RocketQuestion;
  /** The three answers, shuffled. */
  answers: string[];
}

/**
 * The next round: a sentence not yet used this run (§6.4), one of its two questions at random, from
 * any set in any order. `usedSentences` is updated; once every sentence has been used it is cleared
 * and the deck starts over, so a run can never run dry.
 */
export function dealQuestion(
  questions: RocketQuestion[],
  usedSentences: Set<string>,
  rnd: () => number = Math.random,
): DealtQuestion | null {
  if (questions.length === 0) return null;
  let open = questions.filter((q) => !usedSentences.has(q.sentence));
  if (open.length === 0) {
    usedSentences.clear();
    open = questions;
  }
  const q = open[Math.floor(rnd() * open.length)];
  usedSentences.add(q.sentence);
  const outsider = q.outsiders[Math.floor(rnd() * q.outsiders.length)];
  return { q, answers: shuffle([q.right, q.rival, outsider], rnd) };
}

function shuffle<T>(xs: T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
